import { CopyChip } from '@src/components/CopyChip';
import { PageHeader } from '@src/components/shared/PageHeader';
import { Badge } from '@src/components/ui/badge';
import { Card, CardContent } from '@src/components/ui/card';
import { errorMessage } from '@src/lib/errors';
import { formatCost, formatDuration, formatRunDate, formatUtcTooltip, statusBadgeVariant, statusLabel } from '@src/lib/runFormat';
import { cn } from '@src/lib/utils';
import { resources } from '@src/resources';
import { fetchRunById, outcomeLabel, scopeLabel } from '@src/services/opsService';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';

const NUM = new Intl.NumberFormat('en-US');

function truncate(value: string, length = 16): string {
  return value.length > length ? `${value.slice(0, length)}…` : value;
}

function formatRelativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// Same private helper as Runs.tsx's own Logs modal -- duplicated rather than imported, matching
// this codebase's existing convention (see Enrichments.tsx's own runDetailHref comment) for small
// view-local formatters that aren't worth a shared module.
function logLevelClassName(level: string): string {
  if (level === 'error') return 'text-status-critical';
  if (level === 'warn') return 'text-status-warning';
  return 'text-muted-foreground';
}

function RecordRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-2">
      <dt className="w-24 shrink-0 text-[10.5px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="flex min-w-0 items-center gap-2 text-sm">{children}</dd>
    </div>
  );
}

/**
 * The run's own ledger row (`enrichment_runs`), keyed by `runId` -- distinct from `RunDetail.tsx`
 * (the "Enrichment detail" page), which shows the current-state `enrichments` record for the run's
 * (scope, propertyId), not necessarily what this specific run itself produced. This page never has
 * that ambiguity: `/ops/runs?runId=...` is an exact match against the ledger's own primary key, so
 * there's nothing here that could be stale relative to a later run.
 */
export function RunEntry() {
  const [searchParams] = useSearchParams();
  const runId = searchParams.get('runId') ?? '';

  const {
    data: run,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['run-entry', runId],
    queryFn: () => fetchRunById(runId),
    enabled: runId !== '',
  });

  const backToRunsHref = run ? `/runs?scope=${encodeURIComponent(run.scope)}` : '/runs';
  const enrichmentRecordHref = run
    ? `/runs/detail?${new URLSearchParams({
        runId: run.runId,
        propertyId: run.propertyId,
        scope: run.scope,
        createdAt: run.createdAt,
      }).toString()}`
    : undefined;

  return (
    <div>
      <div className="mb-2">
        <Link to={backToRunsHref} className="text-sm text-primary hover:underline">
          {resources.runEntry.backToRuns}
        </Link>
      </div>
      <PageHeader title={resources.runEntry.title} subtitle={run ? `${scopeLabel(run.scope)} · ${run.propertyId}` : ''} />

      {isLoading && <p className="text-sm text-muted-foreground">{resources.runEntry.loading}</p>}
      {isError && (
        <div className="text-sm text-status-critical">
          <p>{resources.runEntry.loadError}</p>
          <p className="mt-1 font-mono text-xs text-muted-foreground">{errorMessage(error)}</p>
        </div>
      )}
      {!isLoading && !isError && run === undefined && (
        <Card>
          <CardContent className="p-6 text-sm text-muted-foreground">{resources.runEntry.notFound}</CardContent>
        </Card>
      )}

      {run && (
        <div className="space-y-4">
          <Card>
            <CardContent className="space-y-2 p-4">
              <span className="text-[10.5px] font-medium uppercase tracking-wide text-muted-foreground">
                {resources.runEntry.runPanelTitle}
              </span>
              <dl className="space-y-1.5">
                <RecordRow label={resources.runEntry.runId}>
                  <span className="truncate font-mono text-muted-foreground">{truncate(run.runId, 24)}</span>
                  <CopyChip label="copy" value={run.runId} />
                </RecordRow>
                <RecordRow label={resources.runEntry.trigger}>
                  <span className="truncate font-mono text-muted-foreground" title={run.trigger}>
                    {truncate(run.trigger, 40)}
                  </span>
                  <CopyChip label="copy" value={run.trigger} />
                </RecordRow>
                <RecordRow label={resources.runEntry.scope}>
                  <span className="font-mono text-muted-foreground">{run.scope}</span>
                </RecordRow>
                <RecordRow label={resources.runEntry.propertyId}>
                  <span className="font-mono text-muted-foreground">{run.propertyId}</span>
                </RecordRow>
                <RecordRow label={resources.runEntry.status}>
                  <Badge variant={statusBadgeVariant(run.status)}>{statusLabel(run.status)}</Badge>
                  {run.result && <Badge variant="secondary">{outcomeLabel(run.result)}</Badge>}
                </RecordRow>
                <RecordRow label={resources.runEntry.duration}>
                  <span className="font-mono">{formatDuration(run.durationMs)}</span>
                </RecordRow>
                <RecordRow label={resources.runEntry.created}>
                  <time dateTime={run.createdAt} title={formatUtcTooltip(run.createdAt)}>
                    {formatRunDate(run.createdAt)}
                  </time>
                </RecordRow>
              </dl>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-2 p-4">
              <span className="text-[10.5px] font-medium uppercase tracking-wide text-muted-foreground">
                {resources.runEntry.resultPanelTitle}
              </span>
              {run.error && (
                <p className="whitespace-pre-wrap font-mono text-xs text-status-critical">{run.error}</p>
              )}
              {!run.error && run.message && (
                <p className="whitespace-pre-wrap font-mono text-xs text-muted-foreground">{run.message}</p>
              )}
              {!run.error && !run.message && (
                <p className="text-sm text-muted-foreground">{resources.runEntry.noResultOrError}</p>
              )}
            </CardContent>
          </Card>

          {run.model && (
            <Card>
              <CardContent className="space-y-2 p-4">
                <span className="text-[10.5px] font-medium uppercase tracking-wide text-muted-foreground">
                  {resources.runEntry.aiMetricsPanelTitle}
                </span>
                <dl className="space-y-1.5">
                  <RecordRow label={resources.runEntry.model}>
                    <span className="font-mono">{run.model}</span>
                  </RecordRow>
                  {(run.inputTokens !== undefined || run.outputTokens !== undefined) && (
                    <RecordRow label={resources.runEntry.tokens}>
                      <span className="font-mono">
                        {NUM.format(run.inputTokens ?? 0)} in · {NUM.format(run.outputTokens ?? 0)} out
                      </span>
                    </RecordRow>
                  )}
                  <RecordRow label={resources.runEntry.cost}>
                    <span className="font-mono">{formatCost(run.costUsd)}</span>
                  </RecordRow>
                  {run.providerGenerationId && (
                    <RecordRow label={resources.runEntry.generationId}>
                      <span className="truncate font-mono text-muted-foreground">
                        {truncate(run.providerGenerationId, 32)}
                      </span>
                      <CopyChip label="copy" value={run.providerGenerationId} />
                    </RecordRow>
                  )}
                </dl>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent className="space-y-2 p-4">
              <span className="text-[10.5px] font-medium uppercase tracking-wide text-muted-foreground">
                {resources.runEntry.logsPanelTitle}
              </span>
              {(run.logMessages ?? []).length === 0 && (
                <p className="text-xs text-muted-foreground">{resources.runEntry.noLogs}</p>
              )}
              {(run.logMessages ?? []).map((line, index) => (
                <div key={index} className="flex items-baseline gap-2.5 font-mono text-xs">
                  <span className={cn('w-10 shrink-0 text-[9.5px] font-bold uppercase', logLevelClassName(line.level))}>
                    {line.level}
                  </span>
                  <span className="flex-1 text-muted-foreground">{line.message}</span>
                  <span className="shrink-0 text-[11px] text-muted-foreground">{formatRelativeTime(line.timestamp)}</span>
                </div>
              ))}
            </CardContent>
          </Card>

          {enrichmentRecordHref && (
            <Link to={enrichmentRecordHref} className="text-sm text-primary hover:underline">
              {resources.runEntry.viewEnrichmentRecord} →
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
